<template>
    <div>
        <!-- Composant spécifique pour le v-else ? ... vu qui peut appeler Factors -->
        <!-- FactorLabel - selectedItems tab. (noeud + modeErreur + autoFilter) - allocations - objectTypes - Variant -->
        <FactorSelectionNodes v-if="FactorNodesVisible" :FactorSelections="factorSelections" :FactorLabel="FactorLabel" :Variant="Variant"
            @CloseSelectionEvent="GoPrevious"
            @EditSelectionEvent="GoNext" 
            @SelectionAutoModeEvent="SelectionAutoMode" />
        
        <!-- Modal pour les affectations de facteurs -->
        <Factors :ref="'Factors'"
            :FactorLabel="FactorLabel"
            :Allocations="allocations"
            :ObjectTypes="objectTypes"
            :Variant="Variant"
            @ClosedEvent="GoPrevious"
            @ApplyedEvent="GoNext" />
    </div>
</template>

<script lang="ts">
	import { Component } from 'vue-property-decorator';

    import TreeNode from '@/types/TreeNode';
	import FactorSelectionNodes from './FactorBlock/FactorSelectionNodes.vue';
	import Factor from '@/types/Config/Factor/Benchmarking/Factor';
    import BaseFactorBlock, { DataAutoNodeChecker } from '../BaseFactorBlock';
    import IFactorBlockFeature from '@/interfaces/Config/Factor/IFactorBlockFeature';
    import FactorSelection from '@/types/Config/Factor/Benchmarking/FactorSelection';

    import Factors from '../Factors.vue';

    /** Machine d'états */
    import { BaseFactorState } from "../BaseFactorState";
    import IContext from "@/interfaces/StateMachine/IContext";
    import CheckedSelectionState from './States/CheckedSelectionState';
     
    /** Block de paramétrage de facteur pour la fonction analysis Benchmarking */
    @Component({
        components: {
			FactorSelectionNodes,
            Factors
        }
    })
    export default class FactorBlock extends BaseFactorBlock<Factor> implements IFactorBlockFeature, IContext<BaseFactorState> {
        
        /** Factors enfant */
        public Factors(): Factors {
			return (this.$refs['Factors'] as Factors);
		}

        /** Indique si on affiche la modale des noeuds sélectionnés */
        private factorNodesVisible: boolean = false;
        /** Indique si on affiche la modale des noeuds sélectionnés */
        public get FactorNodesVisible(): boolean {
            return this.factorNodesVisible;
        }
        /** Indique si on affiche la modale des noeuds sélectionnés */
        public set FactorNodesVisible(value: boolean) {
            this.factorNodesVisible = value;
        }
        
        private factorSelections: FactorSelection[] = [];

        /** * Mise à jour du facteur */
        public UpdateSynFactor(){
            console.debug("Benchmarking/FactorBlock - UpdateSynFactor - factorSelections : %O", this.factorSelections);
            let autoFilter: boolean = true;
            let inWarning: boolean = false;

            for (let selection of this.factorSelections) {
                autoFilter &&= selection.AutoFilter;
                inWarning ||= selection.InWarning;

                console.debug("Benchmarking/FactorBlock - UpdateSynFactor - autoFilter : %O - inWarning : %O", autoFilter, inWarning);
            }

            this.factor.Selections = this.factorSelections;
            this.factor.AutoFilter = autoFilter;
            this.factor.InWarning = inWarning;
        }

        /** Permet de remettre les valeurs par défaut pour les affectations du facteur*/
        public SetAutoMode() {
            console.debug("Benchmarking/FactorBlock - SetAutoMode ");
			
            for (let factorSelection of this.factorSelections){
                let selectionNode: TreeNode = this.InvolvedNodes.find(i => i.Id === factorSelection.NodeId);
                this.SelectionAutoMode(selectionNode);
            }
		}

        /** Permet de remettre les valeurs par défaut pour les affectations du facteur*/
        public SelectionAutoMode(selectionNode: TreeNode) {
            let factorSelection: FactorSelection = this.factorSelections.find(f => f.NodeId == selectionNode.Id);

            // On filtre ici les noeuds entre les bornes du noeud coché + lui-même
            let treeNodes: TreeNode[] = this.filterTreeNodeBound(selectionNode, this.InvolvedNodes);

            let data: DataAutoNodeChecker = this.autoNodesChecker(treeNodes);

            factorSelection.AutoFilter = true;
            factorSelection.Filters = data.AutoCheckedNodeIds;
            factorSelection.InWarning = data.InWarning;

            // Mie à jour du facteur
            this.UpdateSynFactor();
		}

        /** Met à jour la liste des noeuds cochés
         * @param checkedNodeIds Liste des noeuds à cocher
         */
        public UpdateCheckedNodes(selectionNode: TreeNode, checkedNodeIds: string[]) {
            let factorSelection: FactorSelection = this.factorSelections.find(f => f.NodeId == selectionNode.Id);

            // On filtre ici les noeuds entre les bornes du noeud coché + lui-même
            let treeNodes: TreeNode[] = this.filterTreeNodeBound(selectionNode, this.InvolvedNodes);

            let data: DataAutoNodeChecker = this.autoNodesChecker(treeNodes);

            factorSelection.AutoFilter = this.compareListes(checkedNodeIds, data.AutoCheckedNodeIds);
            factorSelection.Filters = checkedNodeIds;
            factorSelection.InWarning = data.InWarning;

            // Si mode manuel, on désactive le mode erreur
			if(!factorSelection.AutoFilter)
				factorSelection.InWarning = false;

            // Mie à jour du facteur
            this.UpdateSynFactor();
        }

        public UpdateByInvolvedNodes(val: TreeNode[]) {
            this.involvedNodes = val;

            let lFactorSelections: FactorSelection[] = [];
            
            // Récupération des éléments sélectionnés dans l'arborescence principale
            let checkedNodes = val.filter(v => v.Selections.findIndex(x => x.Affiliation === 0) != -1);

            for (let checkedNode of checkedNodes){
                // On filtre ici les noeuds entre les bornes du noeud coché + lui-même
                let treeNodes: TreeNode[] = this.filterTreeNodeBound(checkedNode, val);

                let data: DataAutoNodeChecker = this.autoNodesChecker(treeNodes);

                lFactorSelections.push({NodeId: checkedNode.Id, Filters: data.AutoCheckedNodeIds, InWarning: data.InWarning, AutoFilter: true})
            }

            this.factorSelections = lFactorSelections;

            // Mise à jour du facteur sélectionné
            this.UpdateSynFactor();
        }

        /** Permet de récupérer une liste des TreeNode descendants et ancêtres à partir des bornes d'un TreeNode spécifique
         * @param involvedNodes Liste des TreeNode à parcourir
         * @param selectionNode TreeNode de départ
         */
        private filterTreeNodeBound(selectionNode : TreeNode, involvedNodes : TreeNode[]): TreeNode[] {
            let treeNodes: TreeNode[] = []
            for (let involvedNode of involvedNodes) {
                if (selectionNode.LeftBound <= involvedNode.LeftBound && selectionNode.RightBound >= involvedNode.RightBound)
                    treeNodes.push(involvedNode);
                if (selectionNode.LeftBound > involvedNode.LeftBound && selectionNode.RightBound < involvedNode.RightBound)
                    treeNodes.push(involvedNode);
            }

            return treeNodes;
        }

        // #region Gestion Machine d'etat
        /** Instance d'état du facteur */
        protected state: BaseFactorState = {} as BaseFactorState;

        /** Permet de réaliser les changements d'état */
        public TransitionTo(state: BaseFactorState, args = undefined): void {
            console.debug(`BaseFactorBlock - Context : Transition to ${(<any>state).constructor.name}.`);

            this.state = state;
            this.state.SetContext(this);

            this.state.Enter(args);
        }

        /** Sur entrée dans l'état */
        public GoEnter(args?: any): void {
            this.state.Enter(args);
        }

        /** Sur application de l'état pour aller à l'étape suivante */
        public GoNext(args?: any): void{
            this.state.Next(args);
        }

        /** Sur fermeture de l'état */
        public GoPrevious(args?: any): void {
            this.state.Previous(args);
        }
        // #endregion Gestion Machine d'etat
        
        /** 
         * Affiche la modale Factors
         * @description Implémentation de la méthode issue de l'interface IFactorBlockFeature
         * @description Appelé depuis FactorBlock.vue parent
         */
         public StartSelection() {
            /** Initialisation de la machine d'état spécialisée pour la feature analysis en cours */
            this.TransitionTo(new CheckedSelectionState());
        }
    }
</script>

<style scoped>

</style>